// app/lib/legacyStorageCleanup.ts
/**
 * One-time cleanup of all legacy `dorat-*` localStorage keys.
 *
 * Called from entry.client.tsx BEFORE any Zustand store's persist middleware
 * has a chance to read the old key names. We chose a clean break (no data
 * migration) — users re-add cart items, reselect their language, and
 * re-authenticate after this ships. The keys are deleted so the abandoned
 * data doesn't sit around as dead bytes in every customer's browser forever.
 *
 * Idempotent. Wrapped in try/catch so a hostile storage (private-browsing
 * quota exception, sandboxed iframe, locked-down enterprise browsers, etc.)
 * never breaks app boot.
 */
export const LEGACY_KEYS = [
    // Zustand persist keys
    'dorat-auth-storage',
    'dorat-cart-storage',
    'dorat-location-storage',
    // Raw localStorage keys from useSession.ts
    'dorat_session_id',
    'dorat_session_expiry',
    // i18n locale preference
    'dorat.locale',
    // PreviewGate access token
    'dorat_preview_access',
    // Dual-storage token leftovers (Phase 3 also removes the writer; this
    // ensures any user with leftover raw keys from the old code has them
    // swept before then).
    'accessToken',
    'refreshToken',
] as const;

export function purgeLegacyStorageKeys(storage: Storage = window.localStorage): void {
    for (const key of LEGACY_KEYS) {
        try {
            storage.removeItem(key);
        } catch {
            // Private-browsing quota or sandboxed storage; carry on.
        }
    }
}
